import {styled} from '@linaria/react';
import React, {MouseEventHandler} from 'react';
import {COLORS} from '../../../constants/colors';
import {
  desktopAndUpLinaria,
  mediaMobileOnly,
  tabletAndUpLinaria,
} from '../../../constants/customMedia';
import {Z_INDEXES} from '../../../constants/layers';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {Banner} from '../../atoms/Banner/Banner';
import {Icon} from '../../atoms/Icon/Icon';
import {GenericIconName} from '../../atoms/Icon/IconName';
import {
  CampaignBannerTitle,
  Container as CampaignBannerTitleContainer,
} from '../../molecules/CampaignBanner/CampaignBannerTitle';
import {CountdownBoxed} from '../../molecules/Countdown/CountdownBoxed';
import {RowSection} from '../../molecules/SplitView/RowSection';
import {InfoCircleWithDialog} from '../InfoCircle/InfoCircleWithDialog';

export interface DiscountBannerProps {
  className?: string;
  link?: string;
  headlineText?: string;
  conditionText?: string;
  title: string;
  subTitle?: string;
  endDate?: Date;
  backgroundColor?: string;
  reducedSize?: boolean;
  asSimpleBanner?: boolean;
  onInfoClick?: MouseEventHandler;
  onClick?: () => void;
}

const TimeBox = ({
  endDate,
  reducedSize,
}: {
  endDate?: Date;
  reducedSize?: boolean;
}) => {
  if (!endDate || reducedSize) {
    return null;
  }

  return (
    <TimeBoxWrapper data-testid="TimeBoxWrapper">
      <CountdownBoxed
        size="small"
        endDate={endDate}
        backgroundColor="rgba(0,0,0,0.1)"
        light
      />
    </TimeBoxWrapper>
  );
};

const CampaignBannerContent = (props: DiscountBannerProps) => {
  const {onClick, link, title, subTitle, reducedSize, asSimpleBanner, endDate} =
    props;

  return (
    <CampaignBannerContentWrapper
      $reversedOrder={asSimpleBanner}
      onClick={onClick}
    >
      {endDate && <TimeBox reducedSize={reducedSize} endDate={endDate} />}
      <StyledCampaignBannerTitle
        $leftAligned={asSimpleBanner}
        $small={title.length > 50 && !!endDate}
        title={title}
        bannerLink={link}
        reduced
      />
      {subTitle && <SubTitle>{subTitle}</SubTitle>}
    </CampaignBannerContentWrapper>
  );
};

export const DiscountBanner = (props: DiscountBannerProps) => {
  const {
    className,
    onClick,
    link,
    headlineText,
    conditionText,
    asSimpleBanner,
    onInfoClick,
    endDate,
  } = props;

  return (
    <StyledBanner
      className={className}
      $reducedSize={props.reducedSize}
      backgroundColor={props?.backgroundColor}
      dataTestid={asSimpleBanner ? 'SimpleBanner' : 'CampaignBannerpersonal'}
      small
      bannerLink={link}
      onBannerClick={onClick}
      $backgroundColor={props?.backgroundColor}
    >
      {!asSimpleBanner && <SaleIcon asset={GenericIconName.SaleCloud} />}

      <RowSection constrained={true}>
        <Wrapper>
          <CampaignBannerContent {...props} />
          {conditionText && (
            <StyledInfoCircleWithDialog
              header={headlineText}
              conditions={conditionText}
              iconSize={'SS'}
              endsAt={endDate}
              countdownBackgroundColor={props?.backgroundColor}
            />
          )}
          {onInfoClick && (
            <div
              data-info-wrapper
              onClick={onInfoClick}
              data-testid="info-icon"
            >
              <Icon asset={GenericIconName.InfoFilled} size="XS" />
            </div>
          )}
        </Wrapper>
      </RowSection>
    </StyledBanner>
  );
};

const SubTitle = styled.div`
  color: ${COLORS.White};
  font-size: 10px;
  font-weight: bold;
  margin-left: 8px;
`;

const StyledCampaignBannerTitle = styled(CampaignBannerTitle)<{
  $leftAligned?: boolean;
  $small?: boolean;
}>`
  font-size: 12px;
  font-weight: bold;
  margin-left: 10px;

  @media ${mediaMobileOnly} {
    font-size: ${props => (props.$small ? FONT_SIZES.px12 : FONT_SIZES.px12)};
    text-align: ${props => (props.$leftAligned ? 'left' : '')};
    line-height: ${props => (props.$leftAligned ? '1rem' : '')};
    font-weight: ${props => (props.$leftAligned ? FONT_WEIGHTS.light : '')};
    margin-left: ${props => (props.$leftAligned ? 0 : '10px')};
  }

  ${tabletAndUpLinaria(`font-size: ${FONT_SIZES.px14};`)};

  ${desktopAndUpLinaria(`font-size: ${FONT_SIZES.px16};`)};
`;

const StyledInfoCircleWithDialog = styled(InfoCircleWithDialog)`
  &&& {
    position: relative;
    top: 0;
    right: 0;
    width: 20px;
    height: 20px;
    margin-left: 8px;
  }
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  width: 100%;
  justify-content: space-between;
`;

const StyledBanner = styled(Banner)<{
  $backgroundColor?: string;
  $reducedSize?: boolean;
}>`
  z-index: ${Z_INDEXES.DISCOUNT_BANNER};
  height: ${({$reducedSize}) => ($reducedSize ? '36px' : '50px')};
  padding-top: 0;
  padding-bottom: 0;
  background-image: ${({$backgroundColor}) =>
    !$backgroundColor
      ? 'linear-gradient(59deg, #e72d34 -5%, #ffa31a 93%)'
      : ''};
`;

const SaleIcon = styled(Icon)`
  position: absolute;
  top: -50px;
  left: -45px;
  width: 100px;
  height: 100px;
  opacity: 0.15;
`;

const CampaignBannerContentWrapper = styled.div<{$reversedOrder?: boolean}>`
  position: relative;

  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;

  @media ${mediaMobileOnly} {
    justify-content: ${props =>
      props.$reversedOrder ? 'space-between' : 'center'};
    flex-direction: ${props => (props.$reversedOrder ? 'row-reverse' : '')};

    > ${CampaignBannerTitleContainer} {
      flex: 1;
    }
  }
`;

const TimeBoxWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-left: 10px;
`;
